// ** React Imports
import { ReactNode, useState, SyntheticEvent, useRef, useEffect } from 'react'

// ** Next Imports
import Link from 'next/link'

// ** MUI Components
import MuiLink from '@mui/material/Link'
import Button from '@mui/material/Button'
import Box, { BoxProps } from '@mui/material/Box'
import useMediaQuery from '@mui/material/useMediaQuery'
import { styled, useTheme } from '@mui/material/styles'
import Typography, { TypographyProps } from '@mui/material/Typography'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import OutlinedInput from '@mui/material/OutlinedInput'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import CircularProgress, { circularProgressClasses, CircularProgressProps } from '@mui/material/CircularProgress'
import FormHelperText from '@mui/material/FormHelperText'
import { MuiOtpInput } from 'mui-one-time-password-input'
import Divider from '@mui/material/Divider'

// ** Icons Imports
import ChevronLeft from 'mdi-material-ui/ChevronLeft'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import CancelRoundedIcon from '@mui/icons-material/CancelRounded'
import EmailRoundedIcon from '@mui/icons-material/EmailRounded'
import ContactEmergencyRoundedIcon from '@mui/icons-material/ContactEmergencyRounded'
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'

// ** Third Party Imports
import useSWRMutation from 'swr/mutation'
import md5 from 'md5'

// ** Configs
import authConfig from 'src/configs/endpoints/authEndPoints'

// ** Layout Import
import BlankLayout from 'src/@core/layouts/BlankLayout'

// ** Hooks
import { useSettings } from 'src/@core/hooks/useSettings'
import useFetch from 'src/hooks/useFetch'

import { setup } from 'src/lib/csrf'

const RightWrapper = styled(Box)<BoxProps>(({ theme }) => ({
  width: '100%',
  [theme.breakpoints.up('md')]: {
    maxWidth: 400
  },
  [theme.breakpoints.up('lg')]: {
    maxWidth: 450
  }
}))

const BoxWrapper = styled(Box)<BoxProps>(({ theme }) => ({
  width: '100%',
  [theme.breakpoints.down('md')]: {
    maxWidth: 400
  }
}))

const TypographyStyled = styled(Typography)<TypographyProps>(({ theme }) => ({
  fontWeight: 600,
  letterSpacing: '0.18px',
  marginBottom: theme.spacing(1.5),
  [theme.breakpoints.down('md')]: { marginTop: theme.spacing(8) }
}))

const StyledLink = styled('a')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none',
  marginRight: theme.spacing(8)
}))

const StyledImg = styled('img')(() => ({
  height: 30
}))

const CustomCircularProgress = (props: CircularProgressProps) => {
  return (
    <Box sx={{ position: 'relative', width: 20, height: 20 }}>
      <CircularProgress
        variant='determinate'
        sx={{
          position: 'absolute',
          left: 0,
          color: theme => theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800]
        }}
        size={20}
        thickness={8}
        {...props}
        value={100}
      />
      <CircularProgress
        variant='indeterminate'
        disableShrink
        sx={{
          color: theme => theme.myColor?.colorToggle,
          animationDuration: '1000ms',
          position: 'absolute',
          left: 0,
          [`& .${circularProgressClasses.circle}`]: {
            strokeLinecap: 'round'
          }
        }}
        size={20}
        thickness={8}
        {...props}
      />
    </Box>
  )
}

const ForgotPassword = () => {
  // ** Hooks
  const theme = useTheme()
  const { settings } = useSettings()
  const { ReqWithPayload } = useFetch()

  // ** Vars
  const { skin } = settings
  const hidden = useMediaQuery(theme.breakpoints.down('md'))
  const timerIntervalRef = useRef<NodeJS.Timer | null>(null)

  // ** state
  const [timer, setTimer] = useState(0)

  // ** SWR Mutations
  const { trigger: triggerValid } = useSWRMutation(
    `${process.env.NEXT_PUBLIC_BASE_PATH}/api/reqHandler/`,
    ReqWithPayload
  )
  const { trigger: triggerPassword, isMutating: isMutatingPassword } = useSWRMutation(
    `${process.env.NEXT_PUBLIC_BASE_PATH}/api/reqHandler/`,
    ReqWithPayload
  )
  const { trigger: triggerEmail } = useSWRMutation(
    `${process.env.NEXT_PUBLIC_BASE_PATH}/api/mailHandler/`,
    ReqWithPayload
  )

  // ** Email States
  const [emailValue, setEmailValue] = useState<string>('')
  const [emailValid, setEmailValid] = useState<boolean>(false)
  const [emailIsLoading, setEmailIsLoading] = useState<boolean>(false)
  const [emailIsEmpty, setEmailIsEmpty] = useState<boolean>(true)
  const [emailError, setEmailError] = useState<boolean>(false)

  // ** Nik States
  const [nikValue, setNikValue] = useState<string>('')
  const [nikValid, setNikValid] = useState<boolean>(false)
  const [nikIsLoading, setNikIsLoading] = useState<boolean>(false)
  const [nikIsEmpty, setNikIsEmpty] = useState<boolean>(true)
  const [nikError, setNikError] = useState<boolean>(false)

  // ** OTP
  const [otpValid, setOTPValid] = useState<boolean>(false)
  const [otp, setOtp] = useState('')
  const [md5OTP, setMD5OTP] = useState('')

  // ** Password States
  const [userID, setUserID] = useState<string>('')
  const [username, setUsername] = useState<string>('')
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [showPasswordRepeat, setShowPasswordRepeat] = useState<boolean>(false)
  const [passwordValue, setPasswordValue] = useState<string>('')
  const [passwordRepeatValue, setPasswordRepeatValue] = useState<string>('')
  const [passwordValid, setPasswordValid] = useState<boolean>(false)
  const [passwordRepeatValid, setPasswordRepeatValid] = useState<boolean>(false)
  const [passwordError, setPasswordError] = useState<boolean>(false)
  const [passwordRepeatError, setPasswordRepeatError] = useState<boolean>(false)
  const [passwordErrorMessage, setPasswordErrorMessage] = useState<string>('')
  const [passwordRepeatErrorMessage, setPasswordRepeatErrorMessage] = useState<string>('')

  const [changePasswordOK, setChangePasswordOK] = useState<boolean>(false)

  const handleSendOTP = ({ name, OTP, recipient }: { name: string; OTP: string; recipient: string }) => {
    const payload = {
      name,
      OTP,
      recipient,
      subject: 'Kode Verifikasi (OTP) Bankaltimtara Career'
    }

    triggerEmail(payload)
  }

  const getRandomSubstring = (str: string) => {
    // Generate a random starting index between 0 and the maximum possible starting index
    const startIndex = Math.floor(Math.random() * (str.length - 5))

    // Extract the 6-character substring
    return str.substring(startIndex, startIndex + 6)
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const startTimer = () => {
    setTimer(300)

    timerIntervalRef.current = setInterval(() => {
      setTimer(time => {
        if (time === 0) {
          clearInterval(timerIntervalRef.current!)

          return 0
        } else return time - 1
      })
    }, 1000)
  }

  const clearTimer = () => {
    clearInterval(timerIntervalRef.current!)
  }

  const checkID = () => {
    if (emailValue.length == 0 && nikValue.length == 0) {
      setEmailIsEmpty(true)
      setEmailError(true)
      setNikIsEmpty(true)
      setNikError(true)
    } else if (emailValue.length == 0) {
      setEmailIsEmpty(true)
      setEmailError(true)
      setNikIsEmpty(false)
      setNikError(false)
    } else if (nikValue.length == 0) {
      setEmailIsEmpty(false)
      setEmailError(false)
      setNikIsEmpty(true)
      setNikError(true)
    } else {
      setEmailIsEmpty(false)
      setEmailError(false)
      setNikIsEmpty(false)
      setNikError(false)
      setEmailIsLoading(true)
      setNikIsLoading(true)

      const payload = {
        url: authConfig.ResetPasswordValidEndPoint,
        body: { email: emailValue, nik: nikValue },
        method: 'POST'
      }

      triggerValid(payload, {
        onSuccess(data) {
          setEmailIsLoading(false)
          setNikIsLoading(false)
          if (data?.success == true) {
            setEmailError(false)
            setNikError(false)

            // setEmailValid(true)
            // setNikValid(true)
            setUserID(data?.data?.user?.id)
            setUsername(data?.data?.user?.username)

            // const rand = Math.random() * 15
            const tempMD5OTP = parseInt(md5(nikValue).substring(0, 0 + 14), 16).toString()
            setMD5OTP(tempMD5OTP)

            handleSendOTP({
              name: data?.data?.user?.nama_lengkap,
              OTP: getRandomSubstring(tempMD5OTP),
              recipient: data?.data?.user?.email
            })

            setOTPValid(true)

            // startTimer()
          } else {
            setEmailError(true)
            setNikError(true)
            setEmailValid(false)
            setNikValid(false)
          }
        }
      })
    }
  }

  const checkPassword = (value: string) => {
    setPasswordValue(value)

    if (value.length == 0) {
      setPasswordError(true)
      setPasswordErrorMessage('Password tidak boleh kosong')
    } else {
      const conditions = [
        { condition: /^(?=.*[a-z]).*$/, message: 'Password wajib memiliki kombinasi huruf kecil' },
        { condition: /^(?=.*[A-Z]).*$/, message: 'Password wajib memiliki kombinasi huruf kapital' },
        { condition: /^(?=.*\d).*$/, message: 'Password wajib memiliki kombinasi angka' },
        { condition: /^.{8,}$/, message: 'Password minimal terdiri dari 8 karakter' }
      ]

      const notFulfilledConditions = conditions.filter(({ condition, message }) => {
        const isFulfilled = condition.test(value)
        if (!isFulfilled) {
          setPasswordError(true)
          setPasswordErrorMessage(message)
        }

        return !isFulfilled
      })

      const isValid = notFulfilledConditions.length === 0

      if (isValid) {
        setPasswordError(false)
        setPasswordErrorMessage('')
        setPasswordValid(true)
      }
    }
  }

  const checkPasswordRepeat = (value: string) => {
    setPasswordRepeatValue(value)
    if (value.length == 0 || value != passwordValue) {
      setPasswordRepeatError(true)
      setPasswordRepeatErrorMessage('Password harus sama')
    } else {
      setPasswordRepeatError(false)
      setPasswordRepeatErrorMessage('')
      setPasswordRepeatValid(true)
    }
  }

  const submitChangePassword = () => {
    const payload = {
      url: `${authConfig.ChangePasswordEndPoint}/${userID}`,
      body: { password: passwordValue },
      method: 'PATCH'
    }

    triggerPassword(payload, {
      onSuccess(data) {
        if (data?.success == true) {
          setChangePasswordOK(true)
        }
      }
    })
  }

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault()
  }

  useEffect(() => {
    return () => {
      clearTimer()
    }
  }, [])

  const handleOTP = (value: string) => {
    setOtp(value)

    if (value.length == 6 && md5OTP.includes(value)) setOTPValid(true)
    else setOTPValid(false)
  }

  return (
    <Box className='content-right'>
      {!hidden ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            position: 'relative',
            alignItems: 'center',
            justifyContent: 'center',
            img: { height: '80vh' }
          }}
        >
          <img
            src={`${process.env.NEXT_PUBLIC_BASE_PATH}/images/forgot-password.svg`}
            alt='Forgot Password'
            width='100%'
          />
        </Box>
      ) : null}
      <RightWrapper sx={skin === 'bordered' && !hidden ? { borderLeft: `1px solid ${theme.palette.divider}` } : {}}>
        <Box
          sx={{
            p: 7,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'background.paper'
          }}
        >
          <BoxWrapper>
            <Box
              sx={{
                top: 30,
                left: 40,
                display: 'flex',
                position: 'absolute',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Link href='/home' passHref>
                <StyledLink>
                  {!hidden ? (
                    <StyledImg
                      src={`${process.env.NEXT_PUBLIC_BASE_PATH}/images/logo/logo-tittle-ver.svg`}
                      alt='erecrutment Bankaltimtara'
                    />
                  ) : (
                    <StyledImg
                      src={`${process.env.NEXT_PUBLIC_BASE_PATH}/images/logo/logo.svg`}
                      alt='erecrutment Bankaltimtara'
                    />
                  )}
                </StyledLink>
              </Link>
            </Box>
            {!changePasswordOK ? (
              <div>
                <Box sx={{ mb: 6 }}>
                  <TypographyStyled variant='h5'>Lupa Username/Password? 🔒</TypographyStyled>
                  <Typography variant='body2'>
                    Masukkan <b>Email</b> & <b>NIK</b> anda untuk melihat username & mengatur ulang Password Anda.
                  </Typography>
                </Box>
                <form noValidate autoComplete='off' onSubmit={handleSubmit}>
                  <div>
                    <FormControl fullWidth>
                      <InputLabel htmlFor='email' error={emailError}>
                        Email
                      </InputLabel>
                      <OutlinedInput
                        value={emailValue}
                        label='Email'
                        id='email'
                        autoComplete='off'
                        onChange={e => setEmailValue(e.target.value)}
                        error={emailError}
                        endAdornment={
                          <InputAdornment position='end'>
                            <IconButton edge='end' disabled>
                              {emailIsLoading ? (
                                <CustomCircularProgress />
                              ) : emailValid ? (
                                <CheckCircleRoundedIcon color='success' />
                              ) : emailError ? (
                                <CancelRoundedIcon color='error' />
                              ) : null}
                            </IconButton>
                          </InputAdornment>
                        }
                        startAdornment={
                          <InputAdornment position='start' sx={{ width: 40 }}>
                            <EmailRoundedIcon />
                          </InputAdornment>
                        }
                      />
                      <Box sx={{ minHeight: 20, mb: 1 }}>
                        {emailError ? (
                          <FormHelperText sx={{ color: 'error.main', fontSize: '0.7rem', mt: 0, fontWeight: 600 }}>
                            {emailIsEmpty ? 'Email wajib diisi' : 'Email Tidak Terdafatar'}
                          </FormHelperText>
                        ) : null}
                        {!emailError && !emailIsLoading ? (
                          emailValid ? (
                            <FormHelperText sx={{ color: 'success.main', fontSize: '0.7rem', mt: 0, fontWeight: 600 }}>
                              Email Terdaftar
                            </FormHelperText>
                          ) : null
                        ) : null}
                      </Box>
                    </FormControl>

                    <FormControl fullWidth>
                      <InputLabel htmlFor='nik' error={nikError}>
                        NIK / No KTP
                      </InputLabel>

                      <OutlinedInput
                        value={nikValue}
                        label='NIK / No KTP'
                        id='nik'
                        autoComplete='off'
                        onChange={e => setNikValue(e.target.value)}
                        error={nikError}
                        type='number'
                        endAdornment={
                          <InputAdornment position='end'>
                            <IconButton edge='end' disabled>
                              {nikIsLoading ? (
                                <CustomCircularProgress />
                              ) : nikValid ? (
                                <CheckCircleRoundedIcon color='success' />
                              ) : nikError ? (
                                <CancelRoundedIcon color='error' />
                              ) : null}
                            </IconButton>
                          </InputAdornment>
                        }
                        startAdornment={
                          <InputAdornment position='start' sx={{ width: 40 }}>
                            <ContactEmergencyRoundedIcon />
                          </InputAdornment>
                        }
                      />
                      <Box sx={{ minHeight: 20, mb: 1 }}>
                        {nikError ? (
                          <FormHelperText sx={{ color: 'error.main', fontSize: '0.7rem', mt: 0, fontWeight: 600 }}>
                            {nikIsEmpty ? 'NIK wajib diisi' : 'NIK Tidak Terdaftar'}
                          </FormHelperText>
                        ) : null}
                        {!nikError && !nikIsLoading ? (
                          nikValid ? (
                            <FormHelperText sx={{ color: 'success.main', fontSize: '0.7rem', mt: 0, fontWeight: 600 }}>
                              NIK Terdaftar
                            </FormHelperText>
                          ) : null
                        ) : null}
                      </Box>
                    </FormControl>

                    <Button
                      fullWidth
                      size='large'
                      type='submit'
                      variant='contained'
                      sx={{ borderRadius: 8, textTransform: 'capitalize' }}
                      onClick={() => checkID()}
                      disabled={timer > 0}
                      >
                        {timer == 0
                          ? `Verifikasi`
                          : `Kirim Ulang OTP (${Math.floor(timer / 60)
                              .toString()
                              .padStart(2, '0')}:${(timer % 60).toString().padStart(2, '0')})`}
                    </Button>
                  </div>

                  {emailValid && nikValid && (
                    <Box>
                      <Divider sx={{ my: 8 }} />
                      <Box sx={{ px: 2, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant='body2' textAlign='center' sx={{}}>
                            Silahkan masukkan kode <b>OTP</b> yang dikirimkan ke Email Anda.
                          </Typography>
                        </Box>
                        <MuiOtpInput length={6} value={otp} onChange={handleOTP} autoFocus />
                      </Box>
                    </Box>
                  )}
                  {otpValid && (
                    <Box>
                      <Divider sx={{ my: 8 }} />
                      <Box sx={{ mb: 2 }}>
                        <Box
                          sx={{
                            p: 2,
                            my: 4,
                            borderRadius: 1,
                            backgroundColor: theme => theme.myColor?.colorToggleLightReverse,
                            border: theme => `2px solid ${theme.myColor?.colorToggleReverse}`,
                          }}
                        >
                          <Typography variant='body2' textAlign='center' sx={{}}>
                            Username Anda adalah
                          </Typography>
                          <Typography variant='body1' textAlign='center' sx={{fontWeight: 700}}>
                            {username ? username : '-'}
                          </Typography>
                        </Box>
                        <Typography variant='body2' textAlign='center' sx={{}}>
                          Masukkan Password Baru Anda.
                        </Typography>
                      </Box>
                      <FormControl fullWidth>
                        <InputLabel htmlFor='password' error={passwordError}>
                          Password Baru
                        </InputLabel>

                        <OutlinedInput
                          value={passwordValue}
                          label='Password Baru'
                          onChange={e => checkPassword(e.target.value)}
                          onBlur={e => checkPassword(e.target.value)}
                          id='auth-login-v2-password'
                          autoComplete='off'
                          error={passwordError}
                          type={showPassword ? 'text' : 'password'}
                          endAdornment={
                            <InputAdornment position='end'>
                              <IconButton edge='end' disabled>
                                {passwordValid && !passwordError && <CheckCircleRoundedIcon color='success' />}
                              </IconButton>
                              <IconButton
                                edge='end'
                                onMouseDown={e => e.preventDefault()}
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                {showPassword ? <VisibilityRoundedIcon /> : <VisibilityOffRoundedIcon />}
                              </IconButton>
                            </InputAdornment>
                          }
                        />
                        <Box sx={{ minHeight: 20, mb: 1 }}>
                          {passwordError ? (
                            <FormHelperText sx={{ color: 'error.main', fontSize: '0.7rem', mt: 0, fontWeight: 600 }}>
                              {passwordErrorMessage}
                            </FormHelperText>
                          ) : null}
                        </Box>
                      </FormControl>
                      <FormControl fullWidth>
                        <InputLabel htmlFor='passwordRepeat' error={passwordRepeatError}>
                          Ulangi Password
                        </InputLabel>

                        <OutlinedInput
                          value={passwordRepeatValue}
                          label='Ulangi Password'
                          onChange={e => checkPasswordRepeat(e.target.value)}
                          onBlur={e => checkPasswordRepeat(e.target.value)}
                          id='passwordRepeat'
                          autoComplete='off'
                          error={passwordRepeatError}
                          type={showPasswordRepeat ? 'text' : 'password'}
                          endAdornment={
                            <InputAdornment position='end'>
                              <IconButton edge='end' disabled>
                                {passwordRepeatValid && !passwordRepeatError && (
                                  <CheckCircleRoundedIcon color='success' />
                                )}
                              </IconButton>
                              <IconButton
                                edge='end'
                                onMouseDown={e => e.preventDefault()}
                                onClick={() => setShowPasswordRepeat(!showPasswordRepeat)}
                              >
                                {showPasswordRepeat ? <VisibilityRoundedIcon /> : <VisibilityOffRoundedIcon />}
                              </IconButton>
                            </InputAdornment>
                          }
                        />
                        <Box sx={{ height: 20, mb: 1 }}>
                          {passwordRepeatError ? (
                            <FormHelperText sx={{ color: 'error.main', fontSize: '0.7rem', mt: 0, fontWeight: 600 }}>
                              {passwordRepeatErrorMessage}
                            </FormHelperText>
                          ) : null}
                        </Box>
                      </FormControl>
                      <Button
                        fullWidth
                        size='large'
                        type='submit'
                        variant='contained'
                        sx={{
                          mb: 5.25,
                          borderRadius: 8,
                          textTransform: 'capitalize',
                          display: 'flex',
                          justifyContent: 'space-between'
                        }}
                        onClick={() => submitChangePassword()}
                      >
                        <Box sx={{ width: 20 }} />
                        Ubah Password
                        <Box sx={{ width: 20 }}>{isMutatingPassword && <CustomCircularProgress />}</Box>
                      </Button>
                    </Box>
                  )}
                </form>
              </div>
            ) : (
              <div>
                <Box sx={{ mb: 6 }}>
                  <TypographyStyled variant='h5'>Password Berhasil diubah! ✅</TypographyStyled>
                  <Typography variant='body2'>
                    Silahkan masuk menggunakan <b>Username</b> & <b>Password Baru</b> anda.
                  </Typography>
                </Box>
              </div>
            )}
            <Typography sx={{ mt: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Link passHref href='/login'>
                <Typography
                  component={MuiLink}
                  sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', justifyContent: 'center' }}
                >
                  <ChevronLeft sx={{ mr: 1.5, fontSize: '2rem' }} />
                  <span>Back to login</span>
                </Typography>
              </Link>
            </Typography>
          </BoxWrapper>
        </Box>
      </RightWrapper>
    </Box>
  )
}

ForgotPassword.getLayout = (page: ReactNode) => <BlankLayout>{page}</BlankLayout>

ForgotPassword.guestGuard = true
ForgotPassword.authGuard = false

export const getServerSideProps = setup(async () => {
  return { props: {} }
})

export default ForgotPassword
